<template>
    <div v-if="visible" class="time-filter">
        <div class="time-filter-bar">
            <div class="time-filter-fields">
                <template v-if="mode === 'daily' || mode === 'weekly'">
                    <div class="row">
                        <div class="field">
                            <v-menu v-model="openFrom" :close-on-content-click="false" :disabled="disabled">
                                <template #activator="{ props: a }">
                                    <v-text-field v-bind="a" :model-value="displayDate(fromDate)" readonly
                                        :label="labels.from" density="compact" hide-details style="width: 10rem;"
                                        variant="outlined" append-inner-icon="mdi-calendar" />
                                </template>
                                <v-date-picker :model-value="asDate(fromDate)" :disabled="disabled"
                                    @update:model-value="setFromDate" />
                            </v-menu>
                        </div>
                        <div class="field">
                            <v-menu v-model="openTo" :close-on-content-click="false" :disabled="disabled">
                                <template #activator="{ props: a }">
                                    <v-text-field v-bind="a" :model-value="displayDate(toDate)" readonly
                                        :label="labels.to" density="compact" hide-details style="width: 10rem;"
                                        variant="outlined" append-inner-icon="mdi-calendar" />
                                </template>
                                <v-date-picker :model-value="asDate(toDate)" :disabled="disabled"
                                    @update:model-value="setToDate" />
                            </v-menu>
                        </div>
                    </div>
                </template>

                <template v-else-if="mode === 'hourly'">
                    <div class="row">
                        <div class="field">
                            <v-menu v-model="openDay" :close-on-content-click="false" :disabled="disabled">
                                <template #activator="{ props: a }">
                                    <v-text-field v-bind="a" :model-value="displayDate(day)" readonly
                                        :label="labels.day" density="compact" hide-details style="width: 10rem;"
                                        variant="outlined" append-inner-icon="mdi-calendar" />
                                </template>
                                <v-date-picker :model-value="asDate(day)" :disabled="disabled"
                                    @update:model-value="setDay" />
                            </v-menu>
                        </div>
                        <div class="field">
                            <v-text-field :model-value="fromTime" :label="labels.fromHour" readonly density="compact"
                                hide-details style="width: 4rem;" variant="outlined" :disabled="disabled">
                                <v-menu v-model="openFromTime" :close-on-content-click="false" activator="parent"
                                    min-width="0" :disabled="disabled">
                                    <v-time-picker v-model="fromTime" ampm-in-title @update:model-value="emitNow" />
                                </v-menu>
                            </v-text-field>
                        </div>
                        <div class="field">
                            <v-text-field :model-value="toTime" :label="labels.toHour" readonly density="compact"
                                hide-details style="width: 4rem;" variant="outlined" :disabled="disabled">
                                <v-menu v-model="openToTime" :close-on-content-click="false" activator="parent"
                                    min-width="0" :disabled="disabled">
                                    <v-time-picker v-model="toTime" ampm-in-title @update:model-value="emitNow" />
                                </v-menu>
                            </v-text-field>
                        </div>
                    </div>
                </template>

                <template v-else-if="mode === 'monthly'">
                    <div class="row">
                        <div class="field">
                            <v-menu v-model="openFromMonth" :close-on-content-click="false" :disabled="disabled">
                                <template #activator="{ props: a }">
                                    <v-text-field v-bind="a" :model-value="displayMonth(fromMonth)" readonly
                                        :label="labels.from" density="compact" hide-details style="width: 10rem;"
                                        variant="outlined" append-inner-icon="mdi-calendar" />
                                </template>
                                <v-date-picker :model-value="asMonthDate(fromMonth)" :view-mode="monthlyFromViewMode"
                                    :disabled="disabled" @update:view-mode="(v) => clampMonthlyViewMode('from', v)"
                                    @update:month="(m) => onMonthlyPickerMonth('from', m)"
                                    @update:year="(y) => onMonthlyPickerYear('from', y)"
                                    @update:model-value="(v) => onMonthlyPickerModelValue('from', v)" />
                            </v-menu>
                        </div>
                        <div class="field">
                            <v-menu v-model="openToMonth" :close-on-content-click="false" :disabled="disabled">
                                <template #activator="{ props: a }">
                                    <v-text-field v-bind="a" :model-value="displayMonth(toMonth)" readonly
                                        :label="labels.to" density="compact" hide-details style="width: 10rem;"
                                        variant="outlined" append-inner-icon="mdi-calendar" />
                                </template>
                                <v-date-picker :model-value="asMonthDate(toMonth)" :view-mode="monthlyToViewMode"
                                    :disabled="disabled" @update:view-mode="(v) => clampMonthlyViewMode('to', v)"
                                    @update:month="(m) => onMonthlyPickerMonth('to', m)"
                                    @update:year="(y) => onMonthlyPickerYear('to', y)"
                                    @update:model-value="(v) => onMonthlyPickerModelValue('to', v)" />
                            </v-menu>
                        </div>
                    </div>
                </template>

                <template v-else-if="mode === 'yearly'">
                    <div class="row">
                        <div class="field">
                            <v-menu v-model="openFromYear" :close-on-content-click="false" :disabled="disabled">
                                <template #activator="{ props: a }">
                                    <v-text-field v-bind="a" :model-value="displayYear(fromYear)" readonly
                                        :label="labels.from" density="compact" hide-details style="width: 8rem;"
                                        variant="outlined" append-inner-icon="mdi-calendar" />
                                </template>
                                <v-date-picker :model-value="asYearDate(fromYear)" :view-mode="yearlyFromViewMode"
                                    :disabled="disabled" @update:view-mode="(v) => clampYearlyViewMode('from', v)"
                                    @update:year="(y) => onYearlyPickerYear('from', y)"
                                    @update:model-value="setFromYear" />
                            </v-menu>
                        </div>
                        <div class="field">
                            <v-menu v-model="openToYear" :close-on-content-click="false" :disabled="disabled">
                                <template #activator="{ props: a }">
                                    <v-text-field v-bind="a" :model-value="displayYear(toYear)" readonly
                                        :label="labels.to" density="compact" hide-details style="width: 8rem;"
                                        variant="outlined" append-inner-icon="mdi-calendar" />
                                </template>
                                <v-date-picker :model-value="asYearDate(toYear)" :view-mode="yearlyToViewMode"
                                    :disabled="disabled" @update:view-mode="(v) => clampYearlyViewMode('to', v)"
                                    @update:year="(y) => onYearlyPickerYear('to', y)"
                                    @update:model-value="setToYear" />
                            </v-menu>
                        </div>
                    </div>
                </template>
            </div>

            <v-btn v-if="showFetchButton" class="time-filter-fetch" type="plain" variant="tonal" color="primary"
                @click="emitFetch" rounded="lg" size="small">
                <v-icon size="small">mdi-check</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";

const props = defineProps({
    timeGrouping: {
        type: String,
        default: "daily",
    },
    modelValue: {
        type: Object,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    visible: {
        type: Boolean,
        default: true,
    },
    showFetchButton: {
        type: Boolean,
        default: true,
    },
    labels: {
        type: Object,
        default: () => ({
            day: "Date",
            from: "From",
            to: "To",
            fromHour: "From",
            toHour: "To",
            fetch: "Load chart data",
        }),
    },
});

const emit = defineEmits(["update:modelValue", "filter-change", "fetch"]);

const fetchTitle = computed(() => props.labels?.fetch || "Load chart data");

const mode = computed(() => String(props.timeGrouping || "daily").toLowerCase());

const openFrom = ref(false);
const openTo = ref(false);
const openDay = ref(false);
const openFromMonth = ref(false);
const openToMonth = ref(false);
const openFromYear = ref(false);
const openToYear = ref(false);
const openFromTime = ref(false);
const openToTime = ref(false);

/** Vuetify 3: 'month' | 'year' | 'months' — block 'month' (day grid) for month/year-only UX */
const monthlyFromViewMode = ref("months");
const monthlyToViewMode = ref("months");
const yearlyFromViewMode = ref("year");
const yearlyToViewMode = ref("year");

const day = ref("");
const fromDate = ref("");
const toDate = ref("");
const fromMonth = ref("");
const toMonth = ref("");
const fromYear = ref(new Date().getFullYear() - 4);
const toYear = ref(new Date().getFullYear());
const fromTime = ref("00:00");
const toTime = ref("23:59");

/** Mirrors Vuetify month index (0–11) + year while monthly menus are open */
const scratchFrom = ref({ year: null, month: null });
const scratchTo = ref({ year: null, month: null });

watch(openFromMonth, (open) => {
    if (open) {
        monthlyFromViewMode.value = "months";
        const d = parseYmString(fromMonth.value);
        if (d) {
            scratchFrom.value = { year: d.getFullYear(), month: d.getMonth() };
        }
    }
});
watch(openToMonth, (open) => {
    if (open) {
        monthlyToViewMode.value = "months";
        const d = parseYmString(toMonth.value);
        if (d) {
            scratchTo.value = { year: d.getFullYear(), month: d.getMonth() };
        }
    }
});
watch(openFromYear, (open) => {
    if (open) yearlyFromViewMode.value = "year";
});
watch(openToYear, (open) => {
    if (open) yearlyToViewMode.value = "year";
});

/**
 * Monthly: allow months grid + year picker ('year'). Never 'month' (per-day grid).
 * @param {'from'|'to'} which
 * @param {'month'|'months'|'year'} next
 */
function clampMonthlyViewMode(which, next) {
    const vm = which === "from" ? monthlyFromViewMode : monthlyToViewMode;
    if (next === "month") {
        nextTick(() => {
            vm.value = "months";
        });
        return;
    }
    vm.value = next;
}

/**
 * Yearly: only year list. Block month grid and day grid.
 * @param {'from'|'to'} which
 * @param {'month'|'months'|'year'} next
 */
function clampYearlyViewMode(which, next) {
    const vm = which === "from" ? yearlyFromViewMode : yearlyToViewMode;
    if (next === "month" || next === "months") {
        nextTick(() => {
            vm.value = "year";
        });
        return;
    }
    vm.value = next;
}

function pad2(n) {
    return String(n).padStart(2, "0");
}

function formatYmd(d) {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatYm(d) {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

function endOfMonthTimestamp(ym) {
    const d = parseYmString(ym);
    if (!d) return `${ym}-01T23:59:59`;
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return `${ym}-${pad2(lastDay)}T23:59:59`;
}

function endOfYearTimestamp(yearString) {
    return `${yearString}-12-31T23:59:59`;
}

function parseYmdString(s) {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function asDate(ymdString) {
    return parseYmdString(ymdString);
}

function toYmdFromPicker(v) {
    if (!v) return "";
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return formatYmd(d);
}

function displayDate(ymd) {
    if (!ymd) return "";
    const m = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return String(ymd);
    const [, y, mm, dd] = m;
    const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${dd} ${names[Number(mm) - 1] || mm} ${y}`;
}

function parseYmString(s) {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})$/);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, 1);
}

function asMonthDate(ym) {
    return parseYmString(ym);
}

function ymFromPicker(v) {
    if (!v) return "";
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return formatYm(d);
}

function displayMonth(ym) {
    if (!ym) return "";
    const m = String(ym).match(/^(\d{4})-(\d{2})$/);
    if (!m) return String(ym);
    const [, y, mm] = m;
    const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${names[Number(mm) - 1] || mm} ${y}`;
}

function asYearDate(year) {
    const n = Number(year);
    if (!Number.isFinite(n)) return null;
    return new Date(n, 0, 1);
}

function displayYear(year) {
    if (year === "" || year === null || year === undefined) return "";
    return String(year);
}

function setDefaults() {
    const now = new Date();
    day.value = formatYmd(now);
    fromTime.value = `${pad2(now.getHours())}:00`;
    toTime.value = "23:00";

    const start = new Date(now);
    start.setDate(start.getDate() - 6);
    fromDate.value = formatYmd(start);
    toDate.value = formatYmd(now);

    const monthAnchor = new Date(now.getFullYear(), now.getMonth(), 1);
    monthAnchor.setMonth(monthAnchor.getMonth() - 6);
    fromMonth.value = formatYm(monthAnchor);
    toMonth.value = formatYm(now);

    fromYear.value = now.getFullYear() - 4;
    toYear.value = now.getFullYear();
}

// setDefaults();

function buildPayload() {
    const m = mode.value;

    if (m === "hourly") {
        const d = day.value;
        const ft = fromTime.value || "00:00";
        const tt = toTime.value || "23:00";
        return {
            from: `${d}T${ft}:00`,
            to: `${d}T${tt}:00`,
        };
    }

    if (m === "daily" || m === "weekly") {
        return {
            from: fromDate.value,
            to: toDate.value || fromDate.value,
        };
    }

    if (m === "monthly") {
        const fromYm = fromMonth.value;
        const toYm = toMonth.value || fromYm;
        return {
            from: `${fromYm}-01T00:00:00`,
            to: endOfMonthTimestamp(toYm),
        };
    }

    if (m === "yearly") {
        const fromY = String(fromYear.value);
        const toY = String(toYear.value ?? fromYear.value);
        return {
            from: `${fromY}-01-01T00:00:00`,
            to: endOfYearTimestamp(toY),
        };
    }

    return {
        mode: "daily",
        from: fromDate.value,
        to: toDate.value || fromDate.value,
    };
}

function emitNow() {
    let payload = buildPayload();
    payload['timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    emit("update:modelValue", payload);
    emit("filter-change", payload);
}

/** Same payload as v-model; parent should call the backend when this fires. */
function emitFetch() {
    const payload = buildPayload();
    emit("update:modelValue", payload);
    emit("filter-change", payload);
    emit("fetch", payload);
}

function setFromDate(v) {
    fromDate.value = toYmdFromPicker(v);
    emitNow();
}

function setToDate(v) {
    toDate.value = toYmdFromPicker(v);
    emitNow();
}

function setDay(v) {
    day.value = toYmdFromPicker(v);
    emitNow();
}

/**
 * Monthly: Vuetify updates internal month/year on the months grid, but root modelValue
 * often stays on the old day until the day view commits — we clamp day view away, so
 * we must sync YYYY-MM from update:month / update:year (0–11 month index).
 * Year updates are flushed in a microtask so a Dec→Jan arrow step (update:year then
 * update:month) does not briefly commit the old month with the new year.
 * @param {'from'|'to'} which
 */
function flushMonthlyYmFromScratch(which) {
    const scratch = which === "from" ? scratchFrom : scratchTo;
    const monthRef = which === "from" ? fromMonth : toMonth;
    const m = scratch.value.month;
    const y = scratch.value.year;
    if (m == null || y == null || !Number.isFinite(m) || !Number.isFinite(y)) return;
    const next = `${y}-${pad2(m + 1)}`;
    if (next !== monthRef.value) {
        monthRef.value = next;
        emitNow();
    }
}

/**
 * @param {'from'|'to'} which
 * @param {number} monthIndex
 */
function onMonthlyPickerMonth(which, monthIndex) {
    const scratch = which === "from" ? scratchFrom : scratchTo;
    if (monthIndex == null || !Number.isFinite(Number(monthIndex))) return;
    scratch.value = { ...scratch.value, month: Number(monthIndex) };
    flushMonthlyYmFromScratch(which);
}

/**
 * @param {'from'|'to'} which
 * @param {number} yearVal
 */
function onMonthlyPickerYear(which, yearVal) {
    const scratch = which === "from" ? scratchFrom : scratchTo;
    if (yearVal == null || !Number.isFinite(Number(yearVal))) return;
    scratch.value = { ...scratch.value, year: Number(yearVal) };
    queueMicrotask(() => flushMonthlyYmFromScratch(which));
}

/** Fallback if the picker emits a full date */
function onMonthlyPickerModelValue(which, v) {
    const ym = ymFromPicker(v);
    if (!ym) return;
    const monthRef = which === "from" ? fromMonth : toMonth;
    if (ym === monthRef.value) return;
    monthRef.value = ym;
    const d = parseYmString(ym);
    if (d) {
        const scratch = which === "from" ? scratchFrom : scratchTo;
        scratch.value = { year: d.getFullYear(), month: d.getMonth() };
    }
    emitNow();
}

function onYearlyPickerYear(which, yearVal) {
    if (yearVal == null || !Number.isFinite(Number(yearVal))) return;
    const y = Number(yearVal);
    if (which === "from") {
        fromYear.value = y;
        openFromYear.value = false;
    } else {
        toYear.value = y;
        openToYear.value = false;
    }
    emitNow();
}

function setFromYear(v) {
    if (!v) return;
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return;
    fromYear.value = d.getFullYear();
    openFromYear.value = false;
    emitNow();
}

function setToYear(v) {
    if (!v) return;
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return;
    toYear.value = d.getFullYear();
    openToYear.value = false;
    emitNow();
}

watch(
    () => props.modelValue,
    (v) => {
        if (!v || !v.mode) return;
        const m = String(v.mode).toLowerCase();
        if (m === "hourly") {
            if (v.date || v.day) day.value = v.date || v.day;
            if (typeof v.from === "string") {
                const fm = v.from.match(/T(\d{2}:\d{2})/);
                if (fm) fromTime.value = fm[1];
            } else if (Number.isFinite(v.fromHour)) {
                fromTime.value = `${pad2(Number(v.fromHour))}:00`;
            }
            if (typeof v.to === "string") {
                const tm = v.to.match(/T(\d{2}:\d{2})/);
                if (tm) toTime.value = tm[1];
            } else if (Number.isFinite(v.toHour)) {
                toTime.value = `${pad2(Number(v.toHour))}:00`;
            }
        } else if (m === "daily" || m === "weekly") {
            if (typeof v.from === "string") fromDate.value = v.from;
            if (typeof v.to === "string") toDate.value = v.to;
        } else if (m === "monthly") {
            if (typeof v.from === "string") fromMonth.value = v.from;
            if (typeof v.to === "string") toMonth.value = v.to;
        } else if (m === "yearly") {
            if (v.from !== undefined) fromYear.value = Number(v.from);
            if (v.to !== undefined) toYear.value = Number(v.to);
        }
    },
    { deep: true, immediate: true }
);

onMounted(() => {
    emitNow();
});
</script>

<style scoped>
.time-filter {
    padding: 0;
    line-height: 1;
}

.time-filter-bar {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    column-gap: 8px;
    row-gap: 2px;
}

.time-filter-fields {
    min-width: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
}

.time-filter-fetch {
    flex-shrink: 0;
    margin-left: 4px;
}

.row {
    display: flex;
    gap: 6px;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
}

.field {
    display: flex;
    flex-direction: row;
    gap: 3px;
    align-items: center;
}

.label {
    font-size: 10px;
    color: #666;
    line-height: 1;
}

.input {
    padding: 2px 4px;
    border: 1px solid #e6e6e6;
    border-radius: 6px;
    font-size: 13px;
    background: #fff;
}

.number {
    width: 88px;
}

.time-filter :deep(.v-input) {
    --v-input-control-height: 28px;
}

.time-filter :deep(.v-field) {
    min-height: 28px;
}

.time-filter :deep(.v-field__input) {
    min-height: 28px;
    padding-top: 0;
    padding-bottom: 0;
    font-size: 12px;
}

.time-filter :deep(.v-field__append-inner) {
    padding-top: 0;
}

.time-filter :deep(.v-btn.time-filter-fetch) {
    height: 24px !important;
    min-width: 24px !important;
    width: 24px !important;
}
</style>
